import { IdCard, Eye, Phone as PhoneIcon, MapPin, UserCircle, Mail } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DatePicker } from '../ui/date-picker';
import { SectionLabel } from '../ui/section-label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  US_STATES,
  formatDateOfBirthForDisplay,
  normaliseAdminProfileField,
  type AdminProfileFieldKey,
  type AdminProfileFormData,
} from './adminProfileForm.utils';

interface AdminUserProfileFormBodyProps {
  formData: AdminProfileFormData;
  isEditing: boolean;
  onFieldChange: (field: AdminProfileFieldKey, value: string) => void;
}

/**
 * Shared form body used by both the inline admin profile editor on the user
 * details page and the row-level edit modal on the Manage Users list. Required
 * indicators (`*`) here are ADVISORY — they tell the admin which fields the
 * user themselves must complete, but admin saves are never blocked by them.
 * Partial updates are the design.
 */
export function AdminUserProfileFormBody({
  formData,
  isEditing,
  onFieldChange,
}: AdminUserProfileFormBodyProps) {
  const updateField = (field: AdminProfileFieldKey, value: string) => {
    const next = normaliseAdminProfileField(field, value);
    if (next === null) return;
    onFieldChange(field, next);
  };

  return (
    <div className="space-y-6">
      <FieldSection title="Legal Name" icon={<IdCard className="h-3.5 w-3.5" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProfileField
            label="First name"
            requiredForUser
            value={formData.first_name}
            isEditing={isEditing}
            onChange={(v) => updateField('first_name', v)}
          />
          <ProfileField
            label="Last name"
            requiredForUser
            value={formData.last_name}
            isEditing={isEditing}
            onChange={(v) => updateField('last_name', v)}
          />
        </div>
      </FieldSection>

      <FieldSection
        title="Display Name"
        description="Optional — shown publicly instead of legal name"
        icon={<Eye className="h-3.5 w-3.5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProfileField
            label="Display first name"
            value={formData.display_first_name}
            isEditing={isEditing}
            onChange={(v) => updateField('display_first_name', v)}
          />
          <ProfileField
            label="Display last name"
            value={formData.display_last_name}
            isEditing={isEditing}
            onChange={(v) => updateField('display_last_name', v)}
          />
        </div>
      </FieldSection>

      <FieldSection
        title="Contact"
        icon={<PhoneIcon className="h-3.5 w-3.5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProfileField
            label="Email"
            type="email"
            icon={<Mail className="h-3.5 w-3.5" />}
            value={formData.primary_email}
            isEditing={isEditing}
            onChange={(v) => updateField('primary_email', v)}
          />
          <ProfileField
            label="Phone"
            type="tel"
            requiredForUser
            value={formData.phone}
            isEditing={isEditing}
            onChange={(v) => updateField('phone', v)}
          />
        </div>
      </FieldSection>

      <FieldSection title="Address" icon={<MapPin className="h-3.5 w-3.5" />}>
        <div className="space-y-4">
          <ProfileField
            label="Address line 1"
            requiredForUser
            value={formData.address1}
            isEditing={isEditing}
            onChange={(v) => updateField('address1', v)}
          />
          <ProfileField
            label="Address line 2"
            value={formData.address2}
            isEditing={isEditing}
            onChange={(v) => updateField('address2', v)}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <ProfileField
                label="City"
                requiredForUser
                value={formData.city}
                isEditing={isEditing}
                onChange={(v) => updateField('city', v)}
              />
            </div>
            <div>
              <Label className="text-sm">
                State <span className="text-destructive">*</span>
              </Label>
              {isEditing ? (
                <Select
                  value={formData.state || undefined}
                  onValueChange={(v) => updateField('state', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <ReadOnlyValue value={formData.state} />
              )}
            </div>
            <div>
              <ProfileField
                label="ZIP"
                requiredForUser
                value={formData.zip}
                isEditing={isEditing}
                onChange={(v) => updateField('zip', v)}
              />
            </div>
          </div>
        </div>
      </FieldSection>

      <FieldSection
        title="Personal"
        icon={<UserCircle className="h-3.5 w-3.5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">
              Date of birth <span className="text-destructive">*</span>
            </Label>
            {isEditing ? (
              <DatePicker
                value={formData.date_of_birth}
                onChange={(v) => updateField('date_of_birth', v)}
                placeholder="Select date of birth"
              />
            ) : (
              <ReadOnlyValue value={formatDateOfBirthForDisplay(formData.date_of_birth)} />
            )}
          </div>
          <div>
            <Label className="text-sm">
              Gender <span className="text-destructive">*</span>
            </Label>
            {isEditing ? (
              <Select
                value={formData.gender || undefined}
                onValueChange={(v) => updateField('gender', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <ReadOnlyValue value={formData.gender} />
            )}
          </div>
        </div>
      </FieldSection>
    </div>
  );
}

function FieldSection({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <SectionLabel icon={icon} color="muted">
        {title}
      </SectionLabel>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {children}
    </div>
  );
}

function ProfileField({
  label,
  value,
  isEditing,
  requiredForUser,
  type = 'text',
  icon,
  onChange,
}: {
  label: string;
  value: string;
  isEditing: boolean;
  requiredForUser?: boolean;
  type?: string;
  icon?: React.ReactNode;
  onChange?: (value: string) => void;
}) {
  return (
    <div>
      <Label className="text-sm flex items-center gap-1.5">
        {icon}
        <span>
          {label}
          {requiredForUser && (
            <span className="text-destructive ml-1">*</span>
          )}
        </span>
      </Label>
      {isEditing ? (
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
        />
      ) : (
        <ReadOnlyValue value={value} />
      )}
    </div>
  );
}

/**
 * Static read-only display element. We deliberately do NOT use a disabled
 * <input>: a disabled input invites browser autofill ghosting (overlapping
 * suggestion painted on top of the saved value), which the user-facing
 * profile form ran into in the ZIP field. Plain text avoids that surface
 * entirely.
 */
function ReadOnlyValue({ value }: { value: string | null | undefined }) {
  return (
    <div
      className={cn(
        'flex h-10 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm opacity-70',
      )}
    >
      {value ? (
        <span className="truncate">{value}</span>
      ) : (
        <span className="text-muted-foreground/60">—</span>
      )}
    </div>
  );
}
